import { msalInstance } from '@/config/msal.instance';
import { silentRequest } from '@/config/msal.config';

/**
 * Utility functions for retrieving access tokens
 * Now uses MPN tokens instead of MSAL tokens directly
 */

// Cache for the latest acquired token to avoid repeated MSAL calls
let cachedToken: { token: string; expiry: number } | null = null;

/**
 * Get MPN access token from localStorage
 * This is the token obtained from MPN backend after exchanging Microsoft token
 * @returns MPN access token or null
 */
export function getMpnAccessToken(): string | null {
  return localStorage.getItem('mpn_access_token');
}

/**
 * Store MPN access token in localStorage
 * @param token - MPN access token
 */
export function setMpnAccessToken(token: string): void {
  localStorage.setItem('mpn_access_token', token);
}

/**
 * Clear MPN access token from localStorage
 */
export function clearMpnAccessToken(): void {
  localStorage.removeItem('mpn_access_token');
  localStorage.removeItem('mpn_refresh_token');
}

/**
 * Get the MSAL access token using acquireTokenSilent
 *
 * This uses MSAL's proper API to acquire tokens from cache silently.
 * Tokens are cached locally to avoid async issues in axios interceptors.
 *
 * @returns Promise<string | null> Access token or null
 */
export async function acquireMsalAccessToken(): Promise<string | null> {
  try {
    const account = msalInstance.getActiveAccount();

    if (!account) {
      console.warn('MSAL: No active account found');
      return null;
    }

    // Try to acquire token silently from cache
    const response = await msalInstance.acquireTokenSilent({
      ...silentRequest,
      account,
    });

    // Cache the token with expiry
    cachedToken = {
      token: response.accessToken,
      expiry: response.expiresOn
        ? new Date(response.expiresOn).getTime()
        : Date.now() + 3600000, // Default 1 hour if no expiry
    };

    return response.accessToken;
  } catch (error) {
    console.error('MSAL: Failed to acquire token silently:', error);
    return null;
  }
}

/**
 * Get cached access token synchronously
 *
 * This returns a previously cached token if available and not expired.
 * Should be called after acquireMsalAccessToken() has been called at least once.
 *
 * @returns Access token string or null if not cached or expired
 */
export function getMsalAccessToken(): string | null {
  if (!cachedToken) {
    return null;
  }

  // Check if cached token is expired
  if (Date.now() >= cachedToken.expiry) {
    cachedToken = null;
    return null;
  }

  return cachedToken.token;
}

/**
 * Refresh the cached token
 * Should be called periodically or when token is about to expire
 */
export async function refreshCachedToken(): Promise<void> {
  await acquireMsalAccessToken();
}

/**
 * Check if the cached token is about to expire (within 5 minutes)
 * @returns true if token will expire soon or is already expired
 */
export function isTokenExpiringSoon(): boolean {
  if (!cachedToken) {
    return true;
  }

  // Check if token expires within 5 minutes (300000 ms)
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() >= cachedToken.expiry - fiveMinutes;
}

/**
 * Get the token, refreshing it if it's expiring soon
 * This is useful for proactive token refresh
 */
export async function getOrRefreshToken(): Promise<string | null> {
  if (isTokenExpiringSoon()) {
    return await acquireMsalAccessToken();
  }
  return getMsalAccessToken();
}

/**
 * Alternative approach: Get all MSAL tokens from localStorage
 * Useful for debugging token issues
 */
export function getAllMsalTokens(): Record<string, unknown> {
  const tokens: Record<string, unknown> = {};

  try {
    Object.keys(localStorage).forEach(key => {
      if (key.includes('msal') && key.includes('accesstoken')) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            tokens[key] = JSON.parse(value);
          } catch {
            tokens[key] = value;
          }
        }
      }
    });
  } catch (error) {
    console.warn('Failed to retrieve MSAL tokens:', error);
  }

  return tokens;
}
